import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Gift, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) { try { return Number(n).toLocaleString(); } catch { return n; } }

export default function PublicRewards() {
  const { points, programId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isProgram = !!programId;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const res = isProgram
          ? await axios.get(`${API}/public/reward-program/${programId}`)
          : await axios.get(`${API}/public/reward-slab/${points}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError("Could not load rewards. Please try again from your WhatsApp link.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [points, programId, isProgram]);

  return (
    <div className="min-h-screen bg-[#0b1f24] text-white" data-testid="public-rewards">
      {/* Brand header */}
      <div className="bg-gradient-to-br from-teal-800 to-[#0b1f24] px-5 py-8 text-center">
        <div className="inline-flex items-center gap-2 text-teal-300 text-xs uppercase tracking-[0.2em] font-semibold">
          <Sparkles size={14} /> ZIXA Strong · CBC Rewards
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-bold">
          {isProgram ? (data?.program || "Reward Catalogue") : `Rewards worth ${fmt(points)} Points`}
        </h1>
        <p className="mt-2 text-teal-100/80 text-sm max-w-md mx-auto">
          {isProgram
            ? "Reward products available under your current program. Return to WhatsApp and reply with the reward number to redeem."
            : "Redeemable clinic upgrades and gifts you can claim at this slab. Return to WhatsApp and reply with your slab number to redeem."}
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading && (
          <div className="py-20 text-center text-teal-200 inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={18} /> Loading rewards…
          </div>
        )}
        {error && <div className="py-20 text-center text-rose-300">{error}</div>}
        {!loading && !error && data && data.rewards.length === 0 && (
          <div className="py-20 text-center text-teal-200/70">No rewards are published for this slab yet. Please check with your DSE.</div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {data?.rewards?.map((r) => (
            <div key={r.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm hover:border-teal-400/40 transition-colors" data-testid={`public-reward-${r.id}`}>
              <div className="aspect-[4/3] bg-gradient-to-br from-teal-700/30 to-slate-800 grid place-items-center overflow-hidden">
                {r.image_url
                  ? <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                  : <Gift size={40} className="text-teal-300/60" />}
              </div>
              <div className="p-3.5">
                <div className="font-semibold text-[14px] leading-snug">{r.name}</div>
                {r.description && <div className="text-teal-100/60 text-[12px] mt-1 line-clamp-2">{r.description}</div>}
                <div className="mt-2 inline-flex items-center gap-1 text-teal-300 font-semibold text-[13px]">
                  <Sparkles size={13} /> {fmt(r.points)} pts
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center text-teal-200/40 text-[11px] pb-8">
        CBC – Champion Behind the Champions · Powered by ChannelLoyalty.ai
      </div>
    </div>
  );
}
